import './style.modules.scss'

import {
  BrandIcon,
  BrandIconColor,
  BrandIconId,
  BrandIconSize,
  MaterialIcon
} from '@ampcore/adel-react'
import { NotificationType } from '@ampcore/adel-react'
import { AmpcoreMaterialIconId } from '@ampcore/ampcom-contracts'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import xss from 'xss'

import { usePageTitle } from '../../hooks/usePageTitle'
import { Link } from '../../types/Link.type'
import { Toast } from '../Toast/Toast'

export type SocialShareProps = {
  className?: string
  testId?: string
  copyLink?: Omit<Link, 'url'>
  email?: Link
  linkedIn?: Link
  twitter?: Link
  facebook?: Link
}

export const SocialShare = (props: SocialShareProps) => {
  const { copyLink, email, linkedIn, twitter, facebook, className, testId } =
    props
  const emailButtonRef = useRef(null)
  const copyLinkButtonRef = useRef(null)
  const shareFacebookButtonRef = useRef(null)
  const shareXButtonRef = useRef(null)
  const shareLinkedInButtonRef = useRef(null)
  const [isCopyLinkToastVisible, setIsCopyLinkToastVisible] = useState(false)
  const pageTitle = usePageTitle()

  const replaceTitle = useCallback(
    (url?: string) => url?.replace(/%title%/g, pageTitle) ?? '',
    [pageTitle]
  )

  const emailUrl = useMemo(
    () => replaceTitle(email?.url),
    [replaceTitle, email?.url]
  )
  const linkedInUrl = useMemo(
    () => replaceTitle(linkedIn?.url),
    [replaceTitle, linkedIn?.url]
  )
  const twitterUrl = useMemo(
    () => replaceTitle(twitter?.url),
    [replaceTitle, twitter?.url]
  )
  const facebookUrl = useMemo(
    () => replaceTitle(facebook?.url),
    [replaceTitle, facebook?.url]
  )

  const showCopyLinkToast = () => {
    setIsCopyLinkToastVisible(true)
  }

  const hideCopyLinkToast = () => {
    setIsCopyLinkToastVisible(false)
  }

  const metaDescription = useMemo(() => {
    return (
      (document.querySelector('meta[name="description"]') as HTMLMetaElement)
        ?.content || ''
    )
  }, [])

  const handleCopyClick = () => {
    try {
      const tempInput = document.body.appendChild(
        document.createElement('input')
      )
      tempInput.value = window.location.href
      tempInput.select()
      document.execCommand('copy')
      tempInput.parentNode?.removeChild(tempInput)
    } catch (err) {
      console.error('Unable to copy link', err)
    }

    showCopyLinkToast()
  }

  const handleShareButtonClick = (urlTemplate: string) => {
    const url = urlTemplate.replace(
      '%description%',
      encodeURIComponent(metaDescription)
    )
    window.open(url, '_blank')
  }

  const handleLabelButtonClick = (ref: React.RefObject<HTMLElement>) => {
    ref.current?.click()
  }

  const handleMouseEnter = (ref: React.RefObject<HTMLElement>) => {
    ref.current?.classList.add('is-open')
  }

  const handleMouseLeave = (ref: React.RefObject<HTMLElement>) => {
    ref.current?.classList.remove('is-open')
  }

  if (!copyLink && !email && !linkedIn && !twitter && !facebook) {
    return null
  }

  return (
    <section
      className={`SocialShare ${className ?? ''}`}
      data-testid={testId ?? null}
    >
      <h4 className="SocialShare-heading u-textSizeMsrp2">Share</h4>
      <ul className="SocialShare-list">
        {email?.url && (
          <li className="SocialShare-buttonWrapper u-marginBottom0">
            <button
              ref={emailButtonRef}
              className="btn-social-email u-inlineBlock Button Button--secondary Button--secondary--action-blue SocialShare-button u-marginVert8"
              data-analytics="button-analytics"
              aria-label={xss(email.name)}
              onClick={() => handleShareButtonClick(emailUrl)}
              data-testid={testId ? `${testId}-email-button` : null}
            >
              <MaterialIcon
                icon={AmpcoreMaterialIconId.MAIL}
                className="u-textColorActionBlue"
                ariaLabel="Email"
              />
            </button>
            <button
              tabIndex={-1}
              className="SocialShare-label u-marginLeft8 u-textNormal u-posRelative"
              onClick={() => handleLabelButtonClick(emailButtonRef)}
              onMouseEnter={() => handleMouseEnter(emailButtonRef)}
              onMouseLeave={() => handleMouseLeave(emailButtonRef)}
              data-testid={testId ? `${testId}-email-label` : null}
              dangerouslySetInnerHTML={{ __html: xss(email.name) }}
            ></button>
          </li>
        )}
        {copyLink?.name && (
          <li className="SocialShare-buttonWrapper u-marginBottom0">
            <button
              ref={copyLinkButtonRef}
              className="btn-social-copy u-inlineBlock Button Button--secondary Button--secondary--action-blue SocialShare-button u-marginVert8"
              data-analytics={copyLink.analyticsName}
              aria-label={xss(copyLink.name)}
              onClick={handleCopyClick}
              data-testid={testId ? `${testId}-copy-link-button` : null}
            >
              <MaterialIcon
                icon={AmpcoreMaterialIconId.LINK}
                className="u-textColorActionBlue"
                ariaLabel="Copy"
              />
            </button>
            <button
              tabIndex={-1}
              className="SocialShare-label u-marginLeft8 u-textNormal u-posRelative"
              onClick={() => handleLabelButtonClick(copyLinkButtonRef)}
              onMouseEnter={() => handleMouseEnter(copyLinkButtonRef)}
              onMouseLeave={() => handleMouseLeave(copyLinkButtonRef)}
              data-testid={testId ? `${testId}-copy-link-label` : null}
              dangerouslySetInnerHTML={{ __html: xss(copyLink.name) }}
            ></button>
          </li>
        )}
        {facebook?.url && (
          <li className="SocialShare-buttonWrapper u-marginBottom0">
            <button
              ref={shareFacebookButtonRef}
              className="btn-social-facebook u-inlineBlock Button Button--secondary Button--secondary--action-blue SocialShare-button u-marginVert8"
              data-analytics={facebook.analyticsName}
              aria-label={xss(facebook.name)}
              onClick={() => handleShareButtonClick(facebookUrl)}
              data-testid={testId ? `${testId}-facebook-button` : null}
            >
              <BrandIcon
                icon={BrandIconId.SOCIAL_FACEBOOK}
                size={BrandIconSize.SMALL}
                style={{ width: '16px', height: '16px' }}
              />
            </button>
            <button
              tabIndex={-1}
              className="SocialShare-label u-marginLeft8 u-textNormal u-posRelative"
              onClick={() => handleLabelButtonClick(shareFacebookButtonRef)}
              onMouseEnter={() => handleMouseEnter(shareFacebookButtonRef)}
              onMouseLeave={() => handleMouseLeave(shareFacebookButtonRef)}
              data-testid={testId ? `${testId}-facebook-label` : null}
              dangerouslySetInnerHTML={{ __html: xss(facebook.name) }}
            ></button>
          </li>
        )}
        {twitter?.url && (
          <li className="SocialShare-buttonWrapper u-marginBottom0">
            <button
              ref={shareXButtonRef}
              className="btn-social-twitter u-inlineBlock Button Button--secondary Button--secondary--action-blue SocialShare-button u-marginVert8"
              data-analytics={twitter.analyticsName}
              aria-label={xss(twitter.name)}
              onClick={() => handleShareButtonClick(twitterUrl)}
              data-testid={testId ? `${testId}-twitter-button` : null}
            >
              <BrandIcon
                fill={true}
                color={BrandIconColor.ACTION_BLUE}
                className="SocialShare-svg"
                icon={BrandIconId.SOCIAL_X}
                size={BrandIconSize.SMALL}
                style={{ width: '16px', height: '16px' }}
              />
            </button>
            <button
              tabIndex={-1}
              className="SocialShare-label u-marginLeft8 u-textNormal u-posRelative u-textCenter"
              onClick={() => handleLabelButtonClick(shareXButtonRef)}
              onMouseEnter={() => handleMouseEnter(shareXButtonRef)}
              onMouseLeave={() => handleMouseLeave(shareXButtonRef)}
              data-testid={testId ? `${testId}-twitter-label` : null}
              dangerouslySetInnerHTML={{ __html: xss(twitter.name) }}
            ></button>
          </li>
        )}
        {linkedIn?.url && (
          <li className="SocialShare-buttonWrapper u-marginBottom0">
            <button
              ref={shareLinkedInButtonRef}
              className="btn-social-linkedin u-inlineBlock Button Button--secondary Button--secondary--action-blue SocialShare-button u-marginVert8"
              data-analytics={linkedIn.analyticsName}
              aria-label={xss(linkedIn.name)}
              onClick={() => handleShareButtonClick(linkedInUrl)}
              data-testid={testId ? `${testId}-linkedin-button` : null}
            >
              <BrandIcon
                className="SocialShare-svg"
                icon={BrandIconId.SOCIAL_LINKEDIN}
                size={BrandIconSize.SMALL}
                style={{ width: '16px', height: '16px' }}
              />
            </button>
            <button
              tabIndex={-1}
              className="SocialShare-label u-marginLeft8 u-textNormal u-posRelative"
              onClick={() => handleLabelButtonClick(shareLinkedInButtonRef)}
              onMouseEnter={() => handleMouseEnter(shareLinkedInButtonRef)}
              onMouseLeave={() => handleMouseLeave(shareLinkedInButtonRef)}
              data-testid={testId ? `${testId}-linkedin-label` : null}
              dangerouslySetInnerHTML={{ __html: xss(linkedIn.name) }}
            ></button>
          </li>
        )}
      </ul>
      <Toast
        message="Link copied."
        type={NotificationType.CONFIRM}
        visible={isCopyLinkToastVisible}
        onClose={hideCopyLinkToast}
      />
    </section>
  )
}
